import { SerializedConstructor } from '@langchain/core/load/serializable';
import { FormLabel } from '@mui/joy';

import {
  CHAT_MODEL_MAPPING,
  getModelDisplayNameFromIdList,
} from '@/Pages/Playground/Playground.constants';
import { cn } from '@/utils/tailwind';

import { InstructModelBadge } from '../../InstructModelBadge';
import {
  ManifestEditorProps,
  ManifestPreviewProps,
  tableVariantContainerClass,
} from '../../Manifest.utils';
import { ManifestNavigation } from '../../ManifestNavigation';
import { ManifestProviderReplaceWithSwitch } from '../../ManifestSelectProvider';
import { ManifestSlider } from '../../ManifestSlider';
import { ManifestStopEditor } from '../../ManifestStopEditor';
import { ManifestAzureOpenAIEditor } from '../common/ManifestCommonAzureOpenAI';
import { ManifestOpenAISeed } from '../common/ManifestCommonOpenAI';

export function ManifestAzureOpenAI(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs;

  return (
    <>
      <ManifestNavigation navigation={props.navigation} />

      <div
        className={cn(
          'flex flex-col gap-3',
          props.variant === 'table' && tableVariantContainerClass
        )}
      >
        <ManifestProviderReplaceWithSwitch
          variant={props.variant}
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
          isChatModel={false}
          otherModelId={CHAT_MODEL_MAPPING['AzureChatOpenAI'].modelId}
        />

        <ManifestAzureOpenAIEditor {...props} />

        <ManifestSlider
          title="Temperature"
          value={kwargs.temperature}
          min={0}
          max={2}
          step={0.1}
          onChange={(temperature) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, temperature },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Maximum Length"
          value={kwargs.max_tokens == null ? -1 : kwargs.max_tokens}
          min={-1}
          max={2000}
          step={1}
          onChange={(max_tokens) =>
            props.onChange({
              ...props.value,
              kwargs: {
                ...kwargs,
                max_tokens: max_tokens === -1 ? null : max_tokens,
              },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Top P"
          value={kwargs.top_p}
          min={0}
          max={1}
          step={0.01}
          onChange={(top_p) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, top_p },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Presence Penalty"
          value={kwargs.presence_penalty}
          min={0}
          max={2}
          step={0.01}
          onChange={(presence_penalty) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, presence_penalty },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Frequency Penalty"
          value={kwargs.frequency_penalty}
          min={0}
          max={2}
          step={0.01}
          onChange={(frequency_penalty) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, frequency_penalty },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        {!props.readOnly && <ManifestOpenAISeed {...props} />}

        {props.options && (!props.readOnly || !!props.options.stop) && (
          <div className="flex flex-col gap-2">
            <FormLabel>Stop Sequences</FormLabel>
            <ManifestStopEditor
              value={props.options.stop}
              onChange={(stop) =>
                props.onOptionsChange({
                  ...props.options,
                  stop,
                })
              }
              readOnly={props.readOnly}
            />
          </div>
        )}
      </div>
    </>
  );
}

export function ManifestAzureOpenAIPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const deploymentName = props.value.kwargs['deployment_name'] ?? '';

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="font-semibold">{displayLabel}</span>
        {deploymentName && <span>{deploymentName}</span>}
        <InstructModelBadge />
      </div>
    );
  }

  return null;
}
